import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { STATES, stateFromSlug, stateName, stateSlug, citiesIn, cityPath } from "@/lib/constants";
import { useSeo } from "@/lib/seo";
import { Crumbs, FaqSection, ChipLinks, QIResults, ListCta, faqJsonLd } from "@/components/site/LocalBlocks";

import SEO from "@/lib/seoContent.json";

const fill = (s, code) => s.replaceAll("{name}", stateName(code)).replaceAll("{code}", code);
const faqs = (code) => SEO.faqs.map(([q, a]) => [fill(q, code), fill(a, code)]);

const otherStates = (code) => {
  const i = STATES.findIndex(([c]) => c === code);
  return [...STATES.slice(i + 1), ...STATES.slice(0, i)].slice(0, 12).map(([c, n]) => [n, `/1031-exchange/${stateSlug(c)}`]);
};

export default function StatePage() {
  const { stateSlug: slug } = useParams();
  const code = stateFromSlug(slug);
  const name = code ? stateName(code) : "";
  const [qis, setQis] = useState(null);

  const jsonLd = useMemo(() => code && faqJsonLd(faqs(code)), [code]);

  useSeo({
    title: code ? fill(SEO.stateTitle, code) : "State not found",
    description: code ? fill(SEO.stateDescription, code) : "",
    jsonLd,
  });

  useEffect(() => {
    if (!code) {
      return;
    }
    setQis(null);
    api.get("/qis", { params: { state: code } }).then(({ data }) => setQis(data)).catch(() => setQis([]));
  }, [code]);

  if (!code) return <div className="py-32 text-center text-white/60" data-testid="state-not-found">State not found. <Link to="/1031-exchange" className="text-qi-pink">See all states</Link></div>;

  const cities = citiesIn(code);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="state-page">
      <Crumbs items={[["Home", "/"], ["By state", "/1031-exchange"], [name]]} />
      <div className="relative mt-8">
        <div className="absolute -top-24 -right-10 w-80 h-80 bg-qi-pink/15 rounded-full blur-3xl pointer-events-none" />
        <span className="eyebrow inline-flex items-center gap-2"><MapPin size={13} />{name} · 1031 exchange</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-4 max-w-4xl leading-[1.04]" data-testid="state-title">
          Find a 1031 qualified intermediary in <span className="text-qi-pink">{name}</span>
        </h1>
        <p className="text-white/60 mt-5 max-w-2xl leading-relaxed">{fill(SEO.stateIntro, code)}</p>
      </div>

      <div className="mt-12"><QIResults qis={qis} place={name} /></div>
      <ListCta place={name} />
      <FaqSection heading={`1031 exchanges in ${name}: common questions`} items={faqs(code)} />
      {cities.length > 0 && <ChipLinks title={`Top cities in ${name}`} testid="state-cities" links={cities.map(([c]) => [c, cityPath(c, code)])} />}
      <ChipLinks title="Other states" testid="state-other" links={otherStates(code)} all={["All states", "/1031-exchange"]} />
    </div>
  );
}
